import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Package, Home, Settings, Search, Menu, Store, MapPin, User, LogOut } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useUserAuth } from '@/context/UserAuthContext';
import { useWishlist } from '@/context/WishlistContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CountrySelector from '@/components/CountrySelector';
import ThemeToggle from '@/components/ThemeToggle';
import { useState, useCallback } from 'react';

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/track', label: 'Track Order', icon: MapPin },
];

// Logo Component
const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
    <div className="w-8 h-8 bg-foreground rounded flex items-center justify-center">
      <span className="text-background font-bold text-sm">SS</span>
    </div>
    <span className="font-bold text-xl tracking-tight hidden sm:block" style={{ fontFamily: 'Archivo, sans-serif' }}>
      SALES SPACE
    </span>
  </Link>
);

// Desktop Navigation Component
const DesktopNav = ({ currentPath }) => (
  <nav className="hidden md:flex items-center gap-6">
    {NAV_LINKS.map((link) => (
      <Link
        key={link.to}
        to={link.to}
        data-testid={`nav-${link.label.toLowerCase()}`}
        className={`text-sm font-medium transition-colors hover:text-foreground/80 ${
          currentPath === link.to ? 'text-foreground' : 'text-muted-foreground'
        }`}
      >
        {link.label}
      </Link>
    ))}
  </nav>
);

// Search Form Component
const SearchForm = ({ onSearch }) => {
  const [query, setQuery] = useState('');

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim());
      setQuery('');
    }
  }, [query, onSearch]);

  return (
    <form onSubmit={handleSubmit} className="hidden sm:flex items-center">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search products..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 w-48 lg:w-64 h-9 text-sm"
          data-testid="search-input"
        />
      </div>
    </form>
  );
};

const WishlistLink = () => {
  const { wishlistCount } = useWishlist();
  return (
    <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist">
      <Button variant="ghost" size="icon" className="relative">
        <Heart className="w-5 h-5" />
        {wishlistCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500" data-testid="wishlist-count">
            {wishlistCount}
          </Badge>
        )}
      </Button>
    </Link>
  );
};

// Cart Button Component
const CartButton = ({ count }) => (
  <Link to="/cart" data-testid="cart-link">
    <Button variant="ghost" size="icon" className="relative">
      <ShoppingCart className="w-5 h-5" />
      {count > 0 && (
        <Badge 
          className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-[#2563EB]"
          data-testid="cart-count"
        >
          {count}
        </Badge>
      )}
    </Button>
  </Link>
);

// User Profile Button Component
const UserProfileButton = ({ user, onLogout }) => {
  if (!user) {
    return (
      <Link to="/login" data-testid="login-link">
        <Button variant="ghost" size="icon">
          <User className="w-5 h-5" />
        </Button>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="user-menu-button">
          {user.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="text-red-600 cursor-pointer">
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Mobile Navigation Component
const MobileNav = ({ open, onOpenChange, currentPath, onSearch }) => {
  const [query, setQuery] = useState('');

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim());
      setQuery('');
      onOpenChange(false);
    }
  }, [query, onSearch, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild className="md:hidden">
        <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
          <Menu className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72">
        <div className="flex flex-col gap-6 mt-8">
          <form onSubmit={handleSubmit}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                data-testid="mobile-search-input"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => onOpenChange(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`flex items-center gap-3 text-base font-medium py-2 px-3 rounded-md transition-colors ${
                  currentPath === link.to 
                    ? 'bg-secondary text-foreground' 
                    : 'text-muted-foreground hover:bg-secondary/50'
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Footer Component
const Footer = () => (
  <footer className="border-t bg-muted/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 bg-foreground rounded flex items-center justify-center">
              <span className="text-background font-bold text-xs">SS</span>
            </div>
            <span className="font-bold">SALES SPACE</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Your marketplace for physical products and affiliate deals.
          </p>
        </div>
        
        {/* Quick Links */}
        <div>
          <h4 className="font-semibold mb-3">Quick Links</h4>
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <Link to="/products" className="hover:text-foreground">All Products</Link>
            <Link to="/track" className="hover:text-foreground">Track Order</Link>
            <Link to="/cart" className="hover:text-foreground">Shopping Cart</Link>
            <Link to="/wishlist" className="hover:text-foreground">My Wishlist</Link>
          </div>
        </div>
        
        {/* For Sellers */}
        <div>
          <h4 className="font-semibold mb-3">For Sellers</h4>
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <Link to="/merchant/register" data-testid="footer-sell-on-salesspace" className="hover:text-foreground flex items-center gap-1">
              <Store className="w-4 h-4" />
              Sell on SalesSpace
            </Link>
            <Link to="/merchant/login" data-testid="footer-merchant-login" className="hover:text-foreground">Merchant Login</Link>
          </div>
        </div>
        
        {/* Support */}
        <div>
          <h4 className="font-semibold mb-3">Support</h4>
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <a href="mailto:support@salesspace-in.com" className="hover:text-foreground">support@salesspace-in.com</a>
            <Link to="/track" className="hover:text-foreground">Track Order</Link>
            <Link to="/data-deletion" className="hover:text-foreground">Data Deletion</Link>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6 border-t">
        <span className="text-sm text-muted-foreground">
          © 2026 SALES SPACE. All rights reserved.
        </span>
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#2563EB]"></span>
            Physical Products
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#F97316]"></span>
            Affiliate Deals
          </span>
        </div>
      </div>
    </div>
  </footer>
);

const Layout = () => {
  const { getCartCount } = useCart();
  const { user, logout } = useUserAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cartCount = getCartCount();

  const handleSearch = useCallback((query) => {
    navigate(`/products?search=${encodeURIComponent(query)}`);
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <DesktopNav currentPath={location.pathname} />
            <div className="flex items-center gap-2">
              <SearchForm onSearch={handleSearch} />
              <CountrySelector variant="compact" />
              <ThemeToggle variant="dropdown" />
              <UserProfileButton user={user} onLogout={handleLogout} />
              <WishlistLink />
              <CartButton count={cartCount} />
              <MobileNav 
                open={mobileMenuOpen} 
                onOpenChange={setMobileMenuOpen}
                currentPath={location.pathname}
                onSearch={handleSearch}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

export default Layout;
